import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { getRoom, updateRoom } from '../api/rooms';
import dxLogo from '../assets/image/dxinterior-logo.svg';

/**
 * Header Component
 * Top navigation bar with logo, search, and user profile
 * Matches the DX LIVING design
 */

const formatRole = (role) => {
  if (!role) return '';
  const r = String(role).toLowerCase();
  if (r === 'admin') return 'Admin';
  if (r === 'supplier') return 'Supplier';
  if (r === 'user') return 'User';
  if (r === 'vip' || r === 'homeowner') return 'VIP - Homeowner';
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
};

const Header = () => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roomName, setRoomName] = useState('');
  const [roomNameDraft, setRoomNameDraft] = useState('');
  const [isEditingRoomName, setIsEditingRoomName] = useState(false);
  const [roomNameLoading, setRoomNameLoading] = useState(false);
  const [roomNameSaving, setRoomNameSaving] = useState(false);
  const [roomNameError, setRoomNameError] = useState(null);
  const { user, logout } = useAuth();
  const { currentView, roomId, setCurrentView, setRoomId, setRoomImageId } = useSidebar();
  const userMenuRef = useRef(null);
  const roomNameInputRef = useRef(null);
  const displayName = user?.firstName || user?.lastName
    ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
    : user?.username || 'User';
  const displayRole = user?.userRoles?.[0] ? formatRole(user.userRoles[0]) : null;
  const isSupplier = user?.userRoles?.some((role) => String(role).toLowerCase() === 'supplier');
  const isDesignPage = currentView === 'Photorealistic' && roomId != null;

  const handleGoDashboard = () => {
    setRoomId(null);
    setRoomImageId(null);
    setCurrentView('Dashboard');
  };

  useEffect(() => {
    if (!showUserMenu) return;
    const onMouseDown = (e) => {
      const el = userMenuRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showUserMenu]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    setSearchQuery(params.get('q') ?? '');
  }, []);

  useEffect(() => {
    if (!isDesignPage) {
      setIsEditingRoomName(false);
      setRoomName('');
      setRoomNameDraft('');
      setRoomNameError(null);
      return;
    }
    let mounted = true;
    setRoomNameLoading(true);
    setRoomNameError(null);
    getRoom(roomId)
      .then((room) => {
        if (!mounted) return;
        const nextName = room?.name?.trim?.() || `Room #${roomId}`;
        setRoomName(nextName);
        setRoomNameDraft(nextName);
      })
      .catch((err) => {
        if (!mounted) return;
        setRoomName(`Room #${roomId}`);
        setRoomNameDraft(`Room #${roomId}`);
        setRoomNameError(err?.message ?? 'Failed to load room name');
      })
      .finally(() => {
        if (mounted) setRoomNameLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isDesignPage, roomId]);

  useEffect(() => {
    if (!isEditingRoomName) return;
    roomNameInputRef.current?.focus();
    roomNameInputRef.current?.select();
  }, [isEditingRoomName]);

  const commitRoomName = async () => {
    if (!isDesignPage || roomId == null || roomNameSaving) return;
    const nextName = roomNameDraft.trim();
    if (!nextName) {
      setRoomNameDraft(roomName);
      setIsEditingRoomName(false);
      return;
    }
    if (nextName === roomName) {
      setIsEditingRoomName(false);
      return;
    }
    try {
      setRoomNameSaving(true);
      setRoomNameError(null);
      await updateRoom(roomId, { name: nextName });
      setRoomName(nextName);
      setIsEditingRoomName(false);
    } catch (err) {
      setRoomNameError(err?.message ?? 'Failed to update room name');
    } finally {
      setRoomNameSaving(false);
    }
  };

  const goToSearch = ({ replace = false } = {}) => {
    if (typeof window === 'undefined') return;
    const trimmed = searchQuery.trim();
    const nextUrl = trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search';
    if (replace) {
      window.history.replaceState({}, '', nextUrl);
    } else {
      window.history.pushState({}, '', nextUrl);
    }
    setRoomId(null);
    setRoomImageId(null);
    setCurrentView('Search');
    window.dispatchEvent(new Event('dx-search-query-change'));
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasTyped = searchQuery !== '';
    if (!hasTyped) return;

    const id = setTimeout(() => {
      goToSearch({ replace: true });
    }, 250);

    return () => clearTimeout(id);
  }, [searchQuery]);

  return (
    <header className="header-gradient-overlay fixed top-0 left-0 right-0 z-50 h-16 flex items-center">
      <div className="flex justify-between w-full">
      <div className="flex items-center gap-8">
        {/* Logo */}
        <div className="bg-white flex items-center gap-3 w-70 h-16 px-4">
          <button
            type="button"
            onClick={handleGoDashboard}
            className="flex items-center focus:outline-none"
            title="Go to Dashboard"
            aria-label="Go to Dashboard"
          >
            <img src={dxLogo} alt="DX Interiors" className="h-10 w-auto cursor-pointer" />
          </button>
        </div>

        {isDesignPage && (
            <div className="flex items-center justify-start p-5 h-11 bg-white rounded-full ">
              {isEditingRoomName ? (
                <input
                  ref={roomNameInputRef}
                  type="text"
                  value={roomNameDraft}
                  onChange={(e) => setRoomNameDraft(e.target.value)}
                  onBlur={() => {
                    void commitRoomName();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void commitRoomName();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setRoomNameDraft(roomName);
                      setIsEditingRoomName(false);
                    }
                  }}
                  disabled={roomNameSaving}
                  className="w-fit-content rounded-full focus:outline-none focus:ring-0 focus:ring-primary-500 focus:border-transparent"
                  aria-label="Edit room name"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setRoomNameDraft(roomName);
                    setIsEditingRoomName(true);
                  }}
                  className="max-w-full truncate text-left text-sm text-primary-300 hover:text-gray-900"
                  title={roomNameLoading ? 'Loading room name...' : roomName}
                  aria-label="Edit room name"
                >
                  {roomNameLoading ? 'Loading...' : roomName}
                </button>
              )}
              {roomNameError && (
                <p className="mt-0.5 truncate text-[11px] text-red-600" title={roomNameError}>
                  {roomNameError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="flex items-center w-1/2 px-4">
          <div className="flex-1 mr-5">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder={
                  isSupplier
                    ? 'Search projects, products, materials'
                    : 'Search projects'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    goToSearch();
                  }
                }}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Right side icons and user profile */}
          <div className="flex items-center space-x-4">

            {/* Help Icon *-/}
            <button
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Help"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>

            {/* Settings Icon *-/}
            <button
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Settings"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </button>
            */}

            {/* User Profile */}
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
                  <svg
                    className="w-8 h-8 text-gray-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="text-left hidden md:block">
                  <div className="text-sm font-bold text-gray-800">{displayName}</div>
                  {displayRole && (
                    <div className="text-xs text-gray-500">{displayRole}</div>
                  )}
                </div>
                <svg
                  className="w-4 h-4 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <a
                    href="#"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 opacity-50 pointer-events-none"
                  >
                    Profile
                  </a>
                  <a
                    href="#"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 opacity-50 pointer-events-none"
                  >
                    Account Settings
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      logout();
                      setShowUserMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Log Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
